import { useState, useEffect, type FormEvent, type ReactNode } from "react";
import { REVIEWBOOST_V3_UI_ENABLED } from "../../review-boost-v3-ui";
import type {
  ReputationSettingsData,
  AiAgentSettingsData,
  LocationSummary,
  GoogleStatus,
  GoogleAccount,
  GoogleLocation,
} from "../types";
import { PlatformApiError, type PlatformApiClient } from "../../api";
import type { OrganizationContext } from "../../types";

const SETTINGS_SECTIONS = [
  { id: "negocio", label: "Negocio", icon: "business", desc: "Identidad y datos del local" },
  { id: "google", label: "Google Business Profile", icon: "google", desc: "Conexión y sincronización" },
  { id: "notificaciones", label: "Notificaciones", icon: "notifications", desc: "Alertas de reputación" },
] as const;

type SectionId = (typeof SETTINGS_SECTIONS)[number]["id"] | "automation";
const AUTOMATION_SECTION = { id: "automation", label: "Automatización", icon: "settings", desc: "Modo, respuestas y casos sensibles" } as const;

/** The native Google adapter returns a nullable internal ID and SQLite 0/1 flags. */
export function reviewBoostGoogleLocationLinked(location: GoogleLocation): boolean {
  return typeof location.location_id === "string" && location.location_id.length > 0;
}
export function reviewBoostGoogleLocationPrimary(location: GoogleLocation): boolean {
  return location.is_primary === true || location.is_primary === 1;
}

export function SettingsView({
  settings: initialSettings,
  location: initialLocation,
  internalLocations: availableInternalLocations,
  googleStatus: initialGoogleStatus,
  googleStatusVerified = initialGoogleStatus != null,
  googleAccounts: initialGoogleAccounts = [],
  googleAccountsVerified = false,
  googleLocations: initialGoogleLocations = [],
  googleLocationsVerified = false,
  organization,
  initialSection = "negocio",
  slug,
  api,
  base,
  navigate,
  reload,
  automation,
}: {
  settings: ReputationSettingsData;
  location: LocationSummary | null;
  internalLocations?: LocationSummary[];
  googleStatus: GoogleStatus | null;
  googleStatusVerified?: boolean;
  googleAccounts?: GoogleAccount[];
  googleAccountsVerified?: boolean;
  googleLocations?: GoogleLocation[];
  googleLocationsVerified?: boolean;
  organization: OrganizationContext;
  initialSection?: string;
  slug: string;
  api: PlatformApiClient;
  base: string;
  navigate: (path: string) => void;
  reload: () => void;
  /** V4 composes the existing canonical agent controls within Settings. */
  automation?: ReactNode;
}) {
  const sections = automation !== undefined
    ? [AUTOMATION_SECTION, SETTINGS_SECTIONS[1], SETTINGS_SECTIONS[0], SETTINGS_SECTIONS[2]]
    : SETTINGS_SECTIONS;
  const [activeSection, setActiveSection] = useState<SectionId>(() => {
    const found = sections.find((s) => s.id === initialSection);
    return found ? found.id : "negocio";
  });

  useEffect(() => {
    const found = sections.find((s) => s.id === initialSection);
    if (found && found.id !== activeSection) {
      setActiveSection(found.id);
    }
  }, [initialSection]);

  // Show the canonical stored mode without claiming provider/worker execution authority.
  const [automationMode, setAutomationMode] = useState<AiAgentSettingsData["mode"] | null>(null);
  useEffect(() => {
    if (!REVIEWBOOST_V3_UI_ENABLED || automation !== undefined) return;
    let current = true;
    setAutomationMode(null);
    void api.reputation<{ settings: AiAgentSettingsData }>(slug, "/ai-agent/settings")
      .then(({ settings }) => {
        if (current) setAutomationMode(
          settings?.mode === "paused" || settings?.mode === "approval_required" || settings?.mode === "safe_auto"
            ? settings.mode : null,
        );
      })
      .catch(() => { if (current) setAutomationMode(null); });
    return () => { current = false; };
  }, [api, slug]);

  // Business Profile Form
  const [businessDisplayName, setBusinessDisplayName] = useState(
    initialSettings.business_display_name || initialLocation?.name || ""
  );
  const [address, setAddress] = useState(initialLocation?.address || "");
  const [phone, setPhone] = useState(initialLocation?.phone || "");
  const [whatsapp, setWhatsapp] = useState(initialLocation?.whatsapp || "");
  const [website, setWebsite] = useState(initialLocation?.website || "");

  // Notification Form
  const [alertsEnabled, setAlertsEnabled] = useState(
    Boolean(initialSettings.alerts_enabled)
  );
  const [notificationEmail, setNotificationEmail] = useState(
    initialSettings.notification_email || ""
  );

  // States
  const [busy, setBusy] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const internalLocations = availableInternalLocations ?? (initialLocation ? [initialLocation] : []);
  // Multi-location tenants must explicitly choose the internal location; never silently pick the first.
  const [selectedInternalLocationId, setSelectedInternalLocationId] = useState(
    () => internalLocations.length === 1 ? internalLocations[0]!.id : ""
  );

  function notify(msg: string) {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  }

  async function handleSaveBusiness(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrorMessage(null);
    try {
      await api.reputation(slug, "/settings", {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          business_display_name: businessDisplayName.trim() || null,
        }),
      });

      if (initialLocation?.id) {
        await api.reputation(
          slug,
          `/locations/${encodeURIComponent(initialLocation.id)}/profile`,
          {
            method: "PATCH",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              address: address.trim() || null,
              phone: phone.trim() || null,
              whatsapp: whatsapp.trim() || null,
              website: website.trim() || null,
            }),
          }
        );
      }
      notify("Datos del negocio actualizados.");
      reload();
    } catch (cause) {
      setErrorMessage(
        cause instanceof Error ? cause.message : "No se pudieron guardar los cambios."
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveNotifications(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrorMessage(null);
    try {
      await api.reputation(slug, "/settings", {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          alerts_enabled: alertsEnabled,
          notification_email: notificationEmail.trim() || null,
        }),
      });
      notify("Preferencias de notificación guardadas.");
      reload();
    } catch (cause) {
      setErrorMessage(
        cause instanceof Error ? cause.message : "Error al guardar notificaciones."
      );
    } finally {
      setBusy(false);
    }
  }

  // Google actions
  async function handleGoogleConnect() {
    setBusy(true);
    setErrorMessage(null);
    try {
      const res = await api.reputation<{
        adapter?: string;
        mock_complete?: { body: unknown };
        authorize_url?: string;
      }>(slug, "/google/connect/start", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: "{}",
      });

      if (res.adapter === "mock" && res.mock_complete) {
        await api.reputation(slug, "/google/connect/callback", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(res.mock_complete.body),
        });
        notify("Google Business Profile conectado (modo demo).");
        reload();
      } else if (typeof res.authorize_url === "string" && res.authorize_url.length > 0) {
        window.location.href = res.authorize_url;
      } else {
        setErrorMessage("No se recibió un enlace de autorización válido. Actualizá el estado antes de volver a conectar.");
      }
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 502) {
        setErrorMessage("No se pudo iniciar la conexión con Google (502). No se confirmó ninguna autorización. Volvé a comprobar el estado y reintentá; no hace falta desconectar tu cuenta.");
      } else {
        setErrorMessage(
          cause instanceof Error ? cause.message : "Error al conectar Google Business Profile."
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogleSync() {
    if (!isConnected || !googleLocationsVerified || linkedGoogleLocations.length === 0) {
      setErrorMessage("Primero seleccioná y vinculá una ficha al local de tu negocio. No se puede sincronizar una ficha sin vinculación.");
      return;
    }
    setBusy(true);
    setErrorMessage(null);
    try {
      const primaryLoc = linkedGoogleLocations.find(reviewBoostGoogleLocationPrimary) || linkedGoogleLocations[0];
      const res = await api.reputation<{
        reviews_upserted?: number;
        reviews_fetched?: number;
        provider_total_review_count?: number | null;
        pages_fetched?: number;
      }>(
        slug,
        "/google/sync/reviews",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            ...(primaryLoc?.location_id ? { location_id: primaryLoc.location_id } : {}),
            full_history: true,
          }),
        }
      );
      const providerTotal = res.provider_total_review_count;
      notify(
        providerTotal != null
          ? `Sincronización completa: ${providerTotal} reseñas en Google · ${res.reviews_upserted ?? 0} nuevas o actualizadas · ${res.pages_fetched ?? 0} páginas.`
          : `Sincronización completa: ${res.reviews_fetched ?? 0} reseñas leídas · ${res.reviews_upserted ?? 0} nuevas o actualizadas.`
      );
      reload();
    } catch (cause) {
      setErrorMessage(
        cause instanceof Error ? cause.message : "Error al sincronizar reseñas con Google."
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleSelectGoogleLocation(location: GoogleLocation) {
    if (!isConnected || !googleLocationsVerified || !location.id || reviewBoostGoogleLocationLinked(location)) {
      setErrorMessage("Actualizá el estado de Google antes de seleccionar una ficha.");
      return;
    }
    const local = internalLocations.find((item) => item.id === selectedInternalLocationId);
    if (!local) {
      setErrorMessage("Elegí el local interno al que querés asociar la ficha.");
      return;
    }
    if (!window.confirm(
      `¿Vincular la ficha "${location.title}" con "${local.name}"? Esta acción también importará las reseñas disponibles. Verificá que correspondan al mismo negocio.`
    )) return;
    setBusy(true);
    setErrorMessage(null);
    try {
      // Native handler selects a tenant-scoped Google fixture and immediately runs a full review sync.
      const receipt = await api.reputation<{
        sync?: { status?: string; reviews_fetched?: number; reviews_upserted?: number };
      }>(slug, `/google/locations/${encodeURIComponent(location.id)}/select`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ location_id: local.id }),
      });
      if (receipt.sync?.status !== "success") {
        setErrorMessage("La ficha fue seleccionada, pero no se confirmó una sincronización completa. Volvé a comprobar las reseñas antes de repetir la operación.");
      } else {
        notify(`Ficha vinculada a "${local.name}". ${receipt.sync.reviews_fetched ?? 0} reseñas leídas, ${receipt.sync.reviews_upserted ?? 0} importadas o actualizadas.`);
      }
      reload();
    } catch (cause) {
      // Selecting and syncing are not transactional; a failed sync can leave the link persisted.
      // Never retry automatically: require a fresh readback.
      setErrorMessage(cause instanceof Error
        ? `No se pudo completar la vinculación: ${cause.message}. Comprobá el estado antes de reintentar.`
        : "No se confirmó la vinculación. Actualizá el estado antes de reintentar.");
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogleDisconnect() {
    if (!window.confirm("¿Seguro que querés desconectar tu cuenta de Google Business Profile?")) return;
    setBusy(true);
    setErrorMessage(null);
    try {
      await api.reputation(slug, "/google/disconnect", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: "{}",
      });
      notify("Google Business Profile desconectado.");
      reload();
    } catch (cause) {
      setErrorMessage(
        cause instanceof Error ? cause.message : "Error al desconectar Google."
      );
    } finally {
      setBusy(false);
    }
  }

  const statusKnown = googleStatusVerified && initialGoogleStatus != null;
  const isConnected = statusKnown && initialGoogleStatus.status === "connected";
  const googleConnectionLabel = !statusKnown
    ? "Estado sin verificar"
    : isConnected
    ? "Conectado"
    : initialGoogleStatus?.status === "expired" || initialGoogleStatus?.status === "needs_reauth"
      ? "Reconectar Google"
      : initialGoogleStatus?.status === "connecting"
        ? "Conexión en curso"
        : initialGoogleStatus?.status === "error"
          ? "Revisar conexión"
          : "Requiere conexión";
  const googleLocationsCount = initialGoogleLocations.length;
  const googleAccountsCount = initialGoogleAccounts.length;
  const linkedGoogleLocations = googleLocationsVerified
    ? initialGoogleLocations.filter(reviewBoostGoogleLocationLinked) : [];
  const unlinkedGoogleLocations = googleLocationsVerified
    ? initialGoogleLocations.filter((location) => !reviewBoostGoogleLocationLinked(location)) : [];
  const isMockGoogle = initialGoogleStatus?.adapter === "mock";

  return (
    <div className={`rep-settings-view rep-layout-approved${automation !== undefined ? " rbv4-settings" : ""}`} data-layout="RB-CFG-D">
      <header className="page-heading">
        <p className="eyebrow">Configuración</p>
        <h1>Configuración de Review Boost</h1>
        <p>{automation !== undefined ? "Decidí cómo trabaja Review Boost y cuándo recibir avisos." : "Definí los datos de tu local, conexión con Google y alertas de reputación."}</p>
      </header>

      {REVIEWBOOST_V3_UI_ENABLED && automation === undefined && <section className="rbv3-settings-links" aria-label="Automatización">
        <div><h2>Cómo responde la IA</h2><p role="status">Modo guardado: <strong>{automationMode === "paused" ? "Pausado" : automationMode === "approval_required" ? "Con aprobación" : automationMode === "safe_auto" ? "Automático protegido" : "Sin verificar"}</strong>. {automationMode === "approval_required" ? "Cada respuesta requiere tu confirmación." : automationMode === "paused" ? "La automatización está configurada en pausa." : automationMode === "safe_auto" ? "La publicación efectiva depende de las autorizaciones y las exclusiones del servicio." : "Consultá el agente para verificarlo."}</p></div>
        <a href={`${base}/ai-agent`} onClick={(event) => { if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return; event.preventDefault(); navigate(`${base}/ai-agent`); }}>Configurar automatización →</a>
      </section>}
      {successMessage && <div className="success-alert" role="status">{successMessage}</div>}
      {errorMessage && <div className="inline-alert" role="alert">{errorMessage}</div>}

      <div className="rep-settings-layout">
        {/* Navigation Tabs */}
        <aside className="rep-settings-sidebar" aria-label="Secciones de configuración">
          <nav className="rep-settings-nav">
            <p className="rep-settings-nav-kicker">Review Boost</p>
            {(automation !== undefined ? sections : REVIEWBOOST_V3_UI_ENABLED ? [SETTINGS_SECTIONS[1], SETTINGS_SECTIONS[0], SETTINGS_SECTIONS[2]] : SETTINGS_SECTIONS).map((sec) => (
              <button
                key={sec.id}
                type="button"
                className={["rep-settings-nav-item", activeSection === sec.id ? "active" : ""].filter(Boolean).join(" ")}
                aria-pressed={activeSection === sec.id}
                onClick={() => {
                  setActiveSection(sec.id);
                  navigate(`${base}/settings/${sec.id}`);
                }}
              >
                <span className={`rep-settings-icon rep-settings-icon-${sec.icon}`} aria-hidden="true" />
                <div className="rep-settings-nav-text">
                  <strong>{sec.label}</strong>
                  <small>{sec.desc}</small>
                </div>
              </button>
            ))}
          </nav>
          <p className="rbv3-mobile-scroll-hint">Deslizá para ver más ajustes →</p>
        </aside>

        {/* Section Content */}
        <section className="rep-settings-content" aria-label="Opciones de configuración de Review Boost">
          {automation !== undefined && <section className="rbv4-automation" hidden={activeSection !== "automation"} aria-label="Automatización de Review Boost">
            {automation}
          </section>}
          {/* 1. NEGOCIO */}
          {activeSection === "negocio" && (
            <section className="details-card" aria-label="Información del negocio">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Perfil</p>
                  <h2>Información del negocio</h2>
                </div>
              </div>
              <p className="rep-section-desc">
                Estos datos se utilizan para personalizar las respuestas automáticas y dar contexto a tu Agente IA.
              </p>

              <div className="rep-settings-context-row">
                <div>
                  <span>Espacio de ClickDialog</span>
                  <strong>{organization.organization.displayName}</strong>
                </div>
                <button
                  type="button"
                  className="rep-platform-action"
                  onClick={() => navigate(`/o/${organization.organization.slug}/settings`)}
                >
                  Administrar organización →
                </button>
              </div>

              <form onSubmit={handleSaveBusiness}>
                <div className="rep-field-grid">
                  <div className="field">
                    <label htmlFor="cfg-business-name">Nombre visible del local</label>
                    <input
                      id="cfg-business-name"
                      type="text"
                      placeholder="Ej: Café Aurora Central"
                      value={businessDisplayName}
                      onChange={(e) => setBusinessDisplayName(e.target.value)}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="cfg-address">Dirección física</label>
                    <input
                      id="cfg-address"
                      type="text"
                      placeholder="Ej: Av. Corrientes 1234, CABA"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="cfg-phone">Teléfono de atención</label>
                    <input
                      id="cfg-phone"
                      type="text"
                      placeholder="Ej: 011 4567-8900"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="cfg-whatsapp">WhatsApp de contacto</label>
                    <input
                      id="cfg-whatsapp"
                      type="text"
                      placeholder="Ej: +54 9 11 1234 5678"
                      value={whatsapp}
                      onChange={(e) => setWhatsapp(e.target.value)}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="cfg-website">Sitio web oficial</label>
                    <input
                      id="cfg-website"
                      type="url"
                      placeholder="https://..."
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </div>
                </div>

                <div className="button-row" style={{ marginTop: "24px" }}>
                  <button type="submit" className="primary-button" disabled={busy}>
                    {busy ? "Guardando…" : "Guardar información"}
                  </button>
                </div>
              </form>
            </section>
          )}

          {/* 2. GOOGLE */}
          {activeSection === "google" && (
            <section className="details-card" aria-label="Google Business Profile">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Integración</p>
                  <h2>Google Business Profile</h2>
                </div>
                <span className={`rep-status-pill ${isConnected ? "ok" : "warn"}`}>
                  <span className="rep-status-dot" aria-hidden="true" />
                  {googleConnectionLabel}
                </span>
              </div>

              <div className="rep-google-card-body">
                <div className="settings-note rep-google-connection-truth" role="status">
                  <strong>{isConnected ? "Cuenta de Google conectada" : googleConnectionLabel}</strong>
                  <p>
                    {!statusKnown
                      ? "No se pudo verificar el estado de Google. Esto no significa que la cuenta esté desconectada. Volvé a comprobar antes de iniciar otra conexión."
                      : isConnected
                        ? !googleLocationsVerified
                          ? "La cuenta está conectada, pero no se pudo comprobar qué fichas tiene. No equivale a que no existan. Actualizá el estado o revisá los permisos."
                          : googleLocationsCount > 0
                            ? `${googleLocationsCount} ${googleLocationsCount === 1 ? "ficha detectada" : "fichas detectadas"} en esta consulta. Conectar la cuenta y elegir la ficha correcta son pasos distintos.`
                            : "La cuenta está conectada y la consulta de fichas no devolvió ubicaciones. Revisá los permisos de Google; no hace falta desconectar."
                        : "Todavía no se confirmó una conexión activa. Las reseñas de Google no se pueden sincronizar desde esta pantalla hasta que la autorización esté vigente."}
                  </p>
                  {isConnected && (
                    <p className="rep-hint-text">
                      {googleAccountsVerified ? `${googleAccountsCount} cuentas informadas` : "Cuentas sin verificar"} · {googleLocationsVerified ? `${googleLocationsCount} ubicaciones detectadas` : "Ubicaciones sin verificar"}.
                    </p>
                  )}
                </div>
                <div className="rep-field-grid">
                  <div className="field">
                    <label>Cuenta de Google vinculada</label>
                    <p className="rep-static-value">
                      {initialGoogleStatus?.google_user_email
                        || (isConnected ? "Cuenta conectada" : "No hay cuenta conectada")}
                    </p>
                  </div>

                  <div className="field">
                    <label>Fecha de vinculación</label>
                    <p className="rep-static-value">
                      {initialGoogleStatus?.connected_at
                        ? new Date(initialGoogleStatus.connected_at).toLocaleDateString("es-AR")
                        : "Fecha no disponible"}
                    </p>
                  </div>
                </div>

                <div className="button-row" style={{ marginTop: "20px" }}>
                  {!statusKnown ? (
                    <button type="button" className="primary-button" disabled={busy} onClick={reload}>
                      Volver a comprobar Google
                    </button>
                  ) : !isConnected ? (
                    <button
                      type="button"
                      className="primary-button"
                      disabled={busy}
                      onClick={handleGoogleConnect}
                    >
                      {busy ? "Conectando…" : googleConnectionLabel === "Reconectar Google" ? "Reconectar Google Business Profile" : "Conectar Google Business Profile"}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="primary-button"
                        disabled={busy || !googleLocationsVerified || linkedGoogleLocations.length === 0}
                        onClick={handleGoogleSync}
                      >
                        {busy ? "Sincronizando…" : "Sincronizar reseñas ahora"}
                      </button>
                      <button
                        type="button"
                        className="danger-button"
                        disabled={busy}
                        onClick={handleGoogleDisconnect}
                      >
                        Desconectar cuenta
                      </button>
                    </>
                  )}
                </div>

                {isConnected && (!googleLocationsVerified || linkedGoogleLocations.length === 0) && (
                  <p className="rep-hint-text" role="status">
                    {!googleLocationsVerified
                      ? "No se permite sincronizar hasta verificar las fichas. Actualizá esta pantalla."
                      : googleLocationsCount === 0
                        ? "No hay fichas disponibles para vincular; verificá tu cuenta de Google."
                        : "Seleccioná una ficha y vinculala a tu local antes de sincronizar reseñas."}
                  </p>
                )}
                {isConnected && googleLocationsVerified && unlinkedGoogleLocations.length > 0 && (
                  <div className="field rep-google-internal-location">
                    <label htmlFor="rb-google-internal-location">Local de tu negocio para vincular</label>
                    {internalLocations.length > 0 ? (
                      <select
                        id="rb-google-internal-location"
                        value={selectedInternalLocationId}
                        disabled={busy}
                        onChange={(event) => setSelectedInternalLocationId(event.target.value)}
                      >
                        {internalLocations.length > 1 && <option value="">Elegí un local</option>}
                        {internalLocations.map((local) =>
                          <option key={local.id} value={local.id}>{local.name}</option>
                        )}
                      </select>
                    ) : (
                      <p className="rep-hint-text">Primero registrá un local propio. No se puede vincular una ficha sin un local interno.</p>
                    )}
                    <p className="rep-hint-text">
                      {isMockGoogle
                        ? "Modo demo: estas fichas son sintéticas. Vincular una ficha inicia una importación simulada de reseñas."
                        : "Vinculá únicamente una ficha que corresponda a este negocio. La operación iniciará también una importación de reseñas."}
                    </p>
                  </div>
                )}
                {/* List of accounts and locations if connected */}
                {isConnected && (
                  <div className="rep-google-locations-list">
                    <h3>Fichas y ubicaciones detectadas</h3>
                    {!googleLocationsVerified ? (
                      <p className="rep-hint-text">Fichas sin verificar: la consulta no pudo completarse. Volvé a comprobar Google; no hay evidencia de que la lista esté vacía.</p>
                    ) : initialGoogleLocations.length === 0 ? (
                      <p className="rep-hint-text">La consulta fue correcta, pero no devolvió ubicaciones. La conexión de la cuenta sigue activa; verificá sus permisos y las fichas disponibles.</p>
                    ) : (
                      <div className="rep-locations-grid">
                        {initialGoogleLocations.map((loc) => (
                          <div key={loc.id ?? loc.google_location_name ?? loc.title} className="rep-location-badge">
                            <strong>{loc.title}</strong>
                            <span>{loc.address || "Ubicación activa"}</span>
                            {reviewBoostGoogleLocationPrimary(loc) && <span className="rep-primary-tag">Principal</span>}
                            {reviewBoostGoogleLocationLinked(loc)
                              ? <span className="rep-hint-text">Vinculada al local</span>
                              : <button
                                  type="button"
                                  className="rep-platform-action"
                                  disabled={busy || !selectedInternalLocationId || !loc.id}
                                  onClick={() => void handleSelectGoogleLocation(loc)}
                                >
                                  Vincular ficha e importar reseñas
                                </button>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* 3. NOTIFICACIONES */}
          {activeSection === "notificaciones" && (
            <section className="details-card" aria-label="Notificaciones de reputación">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Alertas</p>
                  <h2>Notificaciones de Review Boost</h2>
                </div>
              </div>
              <p className="rep-section-desc">
                Elegí si querés recibir avisos inmediatos cuando ingresen reseñas negativas o delicadas.
              </p>

              <form onSubmit={handleSaveNotifications}>
                <div className="rep-toggle-item rep-toggle-card">
                  <input
                    id="cfg-alerts-toggle"
                    type="checkbox"
                    checked={alertsEnabled}
                    onChange={(e) => setAlertsEnabled(e.target.checked)}
                  />
                  <label htmlFor="cfg-alerts-toggle">
                    <strong>Alertas de reputación activas</strong>
                    <small>Avisos por email cuando se detecten calificaciones de 1–2★ o riesgo de negocio.</small>
                  </label>
                </div>

                <div className="field" style={{ marginTop: "20px" }}>
                  <label htmlFor="cfg-notif-email">Email para alertas de reputación</label>
                  <input
                    id="cfg-notif-email"
                    type="email"
                    placeholder="alertas@tunegocio.com"
                    value={notificationEmail}
                    onChange={(e) => setNotificationEmail(e.target.value)}
                  />
                </div>

                <div className="button-row" style={{ marginTop: "24px" }}>
                  <button type="submit" className="primary-button" disabled={busy}>
                    {busy ? "Guardando…" : "Guardar preferencias"}
                  </button>
                </div>
              </form>
            </section>
          )}

        </section>
      </div>
    </div>
  );
}
